import TableRow from "@mui/material/TableRow";
import { useContext, useState } from "react";
import { ProviderLinkMaterialTableColumns } from "../../../../configs/provider";
import { Material, Provider } from "../../../../data/entities";
import { ROLE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../shared/components/IconButton";
import SearchBarWithSuggestion from "../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import TextBox from "../../../../shared/components/TextBox";
import CustomButton from "../../../../shared/components/TitleRow/CustomButton";
import { api } from "../../../../shared/hooks/api";
import { ContentWrapper, StyledNavLink } from "../../../../shared/styled/TableStyles";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { error } from "../../../../utils/toast";
import { MaterialButtonRow } from "../../../Provider/create/Content/MaterialList/Styled";
import { ContactInfo, ContactName } from "../../../ProviderList/Content/CustomTable/Styled";
import AddProviderModal from "../AddProviderModal";
import { TableItemCounter, TableTitle } from "./Styled";

interface ProviderRowProps {
    item: any;
    extraRowProps: any;
}

const ProviderRow = ({ item, extraRowProps }: ProviderRowProps) => {
    const price = item.price ? item.price : 0;
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const cannotSeeProviderPrice = [
        ROLE.STORAGE_KEEPER,
        ROLE.FIRST_STORAGE_KEEPER,
        ROLE.SECOND_STORAGE_KEEPER,
    ].includes(role);

    const ContactDetail = () => {
        // Ensure contact_list is an array
        if (!Array.isArray(item.contact_list) || item.contact_list.length === 0) return "Chưa có";

        const contactPerson = item.contact_list.find(
            (contact: any) => contact.contact.is_default,
        );

        return (
            <>
                <ContactName>{contactPerson?.contact.name}</ContactName>
                {contactPerson?.contact.phone && (
                    <>
                        {" "}
                        -{" "}
                        <ContactInfo>
                            {contactPerson?.contact.phone}
                        </ContactInfo>
                    </>
                )}
                {contactPerson?.contact.email && (
                    <>
                        {" "}
                        -{" "}
                        <ContactInfo>
                            {contactPerson?.contact.email}
                        </ContactInfo>
                    </>
                )}
            </>
        );
    };

    return (
        <TableRow>
            <StyledTableCell align="left">
                <StyledNavLink to={`/providers/${item.id}/`}>
                    {item.name}
                </StyledNavLink>
            </StyledTableCell>
            <StyledTableCell align="left">{ContactDetail()}</StyledTableCell>
            {
                !cannotSeeProviderPrice && (
                    <StyledTableCell align="left">
                        <TextBox
                            numeric
                            text={formatInternationalNumber(price ?? "")}
                            onChangeText={(value: string) => {
                                const n = parseInternationalToNumber(value, error);
                                if (!Number.isFinite(n)) return;
                                extraRowProps.onChangeMaterialPrice(item, n);
                            }}
                            disabled={!extraRowProps.canEdit}
                        />
                    </StyledTableCell>
                )
            }
            <StyledTableCell align="left">
                <TextBox
                    text={item.note}
                    onChangeText={(value: string) => {
                        extraRowProps.onChangeProviderNote(item, value);
                    }}
                    disabled={!extraRowProps.canEdit}
                />
            </StyledTableCell>
            <StyledTableCell align="left">
                <IconButton
                    onClick={() => extraRowProps.removeItem(item)}
                    disabled={!extraRowProps.canEdit}
                >
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    );
};

interface ProviderListProps {
    materialData: Material;
    mergeMaterial: Function;
}

const ProviderLinkedList = ({
                                materialData,
                                mergeMaterial,
                            }: ProviderListProps) => {
    const { providers } = materialData;
    const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canEdit = [ROLE.MANAGER, ROLE.LOGISTIC_STAFF, ROLE.LOGISTIC_MANAGER].includes(role);

    const [{ data: allProvidersData }, fetchAllProviders] = api.get("/materials/providers/");
    const allProviders = allProvidersData?.results ?? [];
    const totalProviders = allProvidersData?.count ?? 0;

    const handleSearchSelectProvider = (item: Provider, isAdded: boolean) => {
        const alreadyIn = providers.some((p: any) => p.id === item.id);
        if (isAdded && !alreadyIn) {
            mergeMaterial({ providers: [...providers, { ...item, price: 0, note: "" }] });
        } else if (!isAdded && alreadyIn) {
            mergeMaterial({ providers: providers.filter((p: any) => p.id !== item.id) });
        }
    };

    const getTableColumns = () => {
        const baseColumns = ProviderLinkMaterialTableColumns;
        if ([ROLE.STORAGE_KEEPER, ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role)) {
            return [...baseColumns.slice(0, 2), ...baseColumns.slice(3)];
        }
        return baseColumns;
    };

    const tableItems = providers.length === 0 ? [] : providers;

    const onRemoveProvider = (provider: Provider) => {
        const removedProvider = providers.filter(
            (item: any) => item.id != provider.id,
        );
        mergeMaterial({ providers: removedProvider });
    };

    const onChangeProviderNote = (provider: Provider, note: string) => {
        const updateProviders = providers.map((item: any) => {
            if (item.id === provider.id) return { ...item, note: note };
            return item;
        });
        mergeMaterial({ providers: updateProviders });
    };

    const onChangeProviderPrice = (provider: Provider, price: number) => {
        const updateProviders = providers.map((item: any) => {
            if (item.id === provider.id) return { ...item, price: price };
            return item;
        });
        mergeMaterial({ providers: updateProviders });
    };
    return (
        <>
            {isProviderModalOpen && (
                <AddProviderModal
                    selectedProviders={providers}
                    open={isProviderModalOpen}
                    handleClose={() => setIsProviderModalOpen(false)}
                    mergeMaterial={mergeMaterial}
                />
            )}
            <ContentWrapper>
                <TableTitle>
                    Danh sách nhà cung cấp
                    <TableItemCounter>
                        {" "}
                        ({providers.length} nhà cung cấp)
                    </TableItemCounter>
                </TableTitle>

                {canEdit && (
                    <SearchBarWithSuggestion
                        menuList={allProviders}
                        selectedItems={providers}
                        fetchItems={(variables: any) =>
                            fetchAllProviders({ ...variables, page_size: Math.max(totalProviders, 50) })
                        }
                        handleOnclickItem={handleSearchSelectProvider}
                        placeholder="Tìm kiếm nhà cung cấp..."
                    />
                )}

                <CustomTable
                    itemList={tableItems}
                    RowElement={ProviderRow}
                    TableColumns={getTableColumns()}
                    extraRowProps={{
                        removeItem: onRemoveProvider,
                        onChangeMaterialPrice: onChangeProviderPrice,
                        onChangeProviderNote: onChangeProviderNote,
                        canEdit,
                    }}
                />
                {providers.length === 0 &&
                    "Không có nhà cung cấp nào được chọn"}

                {canEdit && (
                    <MaterialButtonRow>
                        <CustomButton
                            title="Thêm Nhà Cung Cấp"
                            onClick={() => setIsProviderModalOpen(true)}
                            variant="contained"
                        />
                    </MaterialButtonRow>
                )}
            </ContentWrapper>
        </>
    );
};

export default ProviderLinkedList;
